import React, { memo, useState } from 'react'
import { Form, FormControl, InputGroup, ListGroup } from 'react-bootstrap'
import { DescriptionBtnWithPopup } from '../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../ui/RequiredMark/RequiredMark'
import { IErrMsg } from './types'
import { FormattedMessage } from 'react-intl'

export interface InputWithSuggestionsDropdownProps {
    name: string
    options: string[]
    state: any
    setState(state: any): any
    touched?: any
    setTouched?(touched: any): any
    errors: any
    errMsg: IErrMsg
    label?: string | null
    tooltipText?: string
    placeholder?: string
    className?: string
    isColumn?: boolean
    isDisabled?: boolean
    isRequired?: boolean
    onEnter?: Function
    onBlur?: Function
    onChange?: Function
}

export const InputWithSuggestionsDropdown = memo(
    function InputWithSuggestionsDropdown({
    name,
    options,
    state,
    setState,
    touched,
    setTouched,
    errors,
    errMsg,
    label,
    tooltipText,
    placeholder,
    className,
    isDisabled,
    isColumn,
    isRequired,
    onBlur,
    onEnter,
    onChange,
}: InputWithSuggestionsDropdownProps) {
    const [showSuggestions, setShowSuggestions] = useState(false)

    const filteredSuggestions = options.filter((option) => {
        if (state[name]) {
            return option.toLowerCase().includes(state[name]?.toLowerCase())
        } else {
            return options
        }
    })

    const handleSuggestionClick = (suggestion: string) => {
        setTouched?.((prev: typeof touched) => ({ ...prev, [name]: true }))
        setState((prev: typeof state) => ({ ...prev, [name]: suggestion }))
        if (typeof onChange === 'function') onChange(suggestion)
        setShowSuggestions(false)
    }

    const handleChange = (e: any) => {
        const newValue = e.target.value
        setTouched?.({ ...touched, [name]: true })
        setState((prev: typeof state) => ({ ...prev, [name]: newValue }))
        if (typeof onChange === 'function') onChange(newValue)
        setShowSuggestions(true)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => setShowSuggestions(false), 1)
        if (typeof onBlur === 'function') onBlur(e)
        else setTouched && setTouched({ ...touched, [name]: true })
    }

    const handleOnKeyDown = (evt: any) => {
        if (evt.key === 'Enter' && typeof onEnter === 'function') {
            onEnter(evt)
        }
    }

    const handleFocus = () => {
        setTimeout(() => setShowSuggestions(true), 100)
    }

    const isInvalid = touched && touched[name] && errors && errors[name]

    return (
        <Form.Group
            style={{ maxWidth: '600px' }}
            className={`${className} ${
                isInvalid && 'mb-4'
            } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
        >
            {label ? (
                <Form.Label
                    style={{ maxWidth: isColumn ? '30%' : 'auto' }}
                    className="label-main mt-1 text-capital pe-2"
                >
                    <span className="label-main-wrapper p-relative">
                        {label}: {isRequired && <RequiredMark />}
                    </span>
                </Form.Label>
            ) : null}
            <div
                style={{ width: '70%' }}
                className={`d-flex ${!isColumn ? 'flex-1' : ''} p-relative`}
            >
                <div style={{ position: 'relative', width: '100%' }}>
                    <span className="d-flex flex-column w-100">
                        <InputGroup>
                            <FormControl
                                className={`input-main ${
                                    tooltipText && 'input-padding'
                                }`}
                                placeholder={placeholder}
                                value={state?.[name] ?? ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onFocus={handleFocus}
                                disabled={!!isDisabled}
                                isInvalid={isInvalid}
                                onKeyDown={handleOnKeyDown}
                            />
                            <Form.Control.Feedback
                                className="input-feedback"
                                type="invalid"
                            >
                                <FormattedMessage
                                    id={
                                        errMsg?.message ||
                                        'field-validation-error'
                                    }
                                    values={errMsg?.values}
                                    tagName="span"
                                />
                            </Form.Control.Feedback>
                        </InputGroup>
                    </span>
                    {showSuggestions && filteredSuggestions.length > 0 && (
                        <ListGroup
                            style={{
                                position: 'absolute',
                                width: '100%',
                                zIndex: 1000,
                                maxHeight: '200px',
                                overflowY: 'auto',
                            }}
                        >
                            {filteredSuggestions.map((suggestion, i) => (
                                <ListGroup.Item
                                    key={i}
                                    action
                                    onMouseDown={() =>
                                        handleSuggestionClick(suggestion)
                                    }
                                >
                                    {suggestion}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </div>
                {tooltipText && (
                    <DescriptionBtnWithPopup
                        type={'default'}
                        header={label ?? ''}
                        description={tooltipText ?? ''}
                        className="mt-1 input-tooltip"
                    />
                )}
            </div>
        </Form.Group>
    )
})
